import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

export type SettingsModalState = {
  open: boolean;
  /** The matching `SettingsItem.href` from useSettingsList, e.g. `/settings/account`. */
  route: string;
  /** Optional in-page hash like `#about-you` (drives section scroll). */
  hash?: string;
};

export const settingsModalSourceAtom = atom<string | undefined>(undefined);

export const DEFAULT_SETTINGS_MODAL_ROUTE = '/settings/account';

/**
 * localStorage key for the persisted last-route. Exported so
 * `clearLogoutStorage` can drop the entry on full logout and prevent leaking
 * the previous user's last section on a shared browser.
 */
export const LAST_SETTINGS_ROUTE_STORAGE_KEY = 'heidi.settings-modal.last-route';

/**
 * Last route the user opened the settings modal to. Persisted across reloads
 * so reopening returns to the same place ("persistent dialog" UX). Cleared on
 * logout by `clearLogoutStorage` — that, plus the app's session-inactivity
 * timeout / token-expiry logout paths (which all funnel through
 * `clearLogoutStorage`), is sufficient lifecycle for a low-signal preference
 * like this one.
 *
 * Cross-tab updates (another tab writes to this key) flow through Jotai's
 * built-in `storage` event subscription — no extra wiring needed.
 */
export const lastSettingsRouteAtom = atomWithStorage<string>(
  LAST_SETTINGS_ROUTE_STORAGE_KEY,
  DEFAULT_SETTINGS_MODAL_ROUTE
);

/**
 * Live state of the global settings modal (open/close + active section).
 *
 * Bidirectionally synced with the `?settings=<slug>` URL query param by
 * `useSettingsModalUrlSync` — the URL is treated as the source of truth on
 * external navigations (popstate, deep links, route changes), and atom
 * transitions write through to the URL via `history.pushState`/`replaceState`
 * (Next's router is intentionally bypassed to avoid re-rendering the page
 * tree behind the modal).
 */
export const settingsModalAtom = atom<SettingsModalState>({
  open: false,
  route: DEFAULT_SETTINGS_MODAL_ROUTE,
});

/**
 * The open flag alone, so subscribers that only care whether it's open don't
 * re-render on every route/hash change.
 */
export const isSettingsModalOpenAtom = atom((get) => get(settingsModalAtom).open);
